import { createSelector } from 'reselect';
import CustomModal from '../../modals';
import { deleteLeadsThunk } from '../../../redux/thunks/lead';
import React from 'react';
import { red } from '../../../theme/themePrimitives';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: ReviewProspect Component
 * @paragraph: This component is used to review selected prospects and optionally push them into a pipeline and stage.
 * It provides the ability to select a pipeline, a stage, and choose actions for unselected prospects.
 * @interface: ReviewProspectProps
 * @property: {boolean} open - Controls the visibility of the modal.
 * @property: {() => void} onClose - Function to close the modal.
 * @property: {number} totalCount - Total count of prospects available for review.
 * @property: {number[]} selectedRows - Array of selected prospect IDs.
 * @property: {() => void} setIsProspectOpen - Function to manage the state of the prospect view.
 */

interface ReviewProspectProps {
    open: boolean;
    onClose: () => void;
    selectedRows: number[];
}


const loaders = (state: RootState) => state.lead.loaders;

const selectedState = createSelector(
    [loaders],
    (leadLoaders) => ({
        leadLoaders
    })
);

/**
 * @heading2: ReviewProspect
 */
const DeleteleadsModal: React.FC<ReviewProspectProps> = ({
    open,
    onClose,
    selectedRows
}) => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const { leadLoaders } = useSelector(selectedState);

    const submitHandler = async () => {
        try {
            onClose();
            await dispatch(deleteLeadsThunk({ client_id: id ? parseInt(id as string, 10) : 0, ids: selectedRows })).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    return (
        <CustomModal title="Delete Leads" isError={true} open={open} handleClose={leadLoaders.isDeleting ? () => null : onClose}>
            <Typography variant="body1" align="center" mb={2}>Are you sure you want to delete &quot;<Typography component="span" variant="body1" color={red[300]}>{selectedRows.length}</Typography>&quot; Leads?</Typography>
            <Box display="flex" alignItems="center" gap="10px">
                <Button onClick={onClose} variant="outlined" color="secondary" fullWidth disabled={leadLoaders.isDeleting}>
                    Cancel
                </Button>
                <Button
                    onClick={submitHandler}
                    variant="contained"
                    color="error"
                    disabled={leadLoaders.isDeleting}
                    fullWidth
                >
                    {leadLoaders.isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
            </Box>
        </CustomModal>
    );
};

export default DeleteleadsModal;
